import React from 'react'
import axios from 'axios'
import { getApiFromEnv } from '../../Utilities/utilities'
import { useOktaAuth } from '@okta/okta-react'
import { useAppState } from '../../Context/AppProvider'
import {
  BLOCKFI_ACCOUNTS,
  BLOCKFI_SOURCES,
  UPHOLD_ACCOUNTS,
  UPHOLD_SOURCES,
} from '../Helpers/accountTypes'

const useFetch = () => {
  const { authState } = useOktaAuth()
  const appState = useAppState()
  const BASE_URL = getApiFromEnv()
  const AUTH_TOKEN = authState.accessToken.accessToken
  const TENANT_ID = authState?.accessToken?.claims.tenantId // appState.tenant.id

  const wait = (ms) => new Promise((r) => setTimeout(r, ms))

  const fetchUrl = React.useCallback(
    (url, ...params) => {
      const authBearer =
        TENANT_ID !== null
          ? `Bearer ${AUTH_TOKEN}:${TENANT_ID}`
          : `Bearer ${AUTH_TOKEN}`

      const updateCustomerMailing = (customer, preferences) => {
        const authBearer =
          TENANT_ID !== null
            ? `Bearer ${AUTH_TOKEN}:${TENANT_ID}`
            : `Bearer ${AUTH_TOKEN}`
        let data = preferences
        return axios
          .patch(`${BASE_URL}/customers/${customer.uuid}`, data, {
            headers: { Authorization: authBearer },
          })
          .then((response) => {
            return response.data
          })
          .catch((error) => {
            console.error(
              'Customer mailing preference failed to update =>',
              error
            )
            return null
          })
      }

      const fetchCustomerAssets = (customerID) => {
        return axios
          .get(`${BASE_URL}/customer/${customerID}/accounts`, {
            headers: { Authorization: authBearer },
          })
          .then((response) => {
            return response.data.accounts
          })
          .catch((error) => {
            console.error('Fetch customer assets =>', error)
            return []
          })
      }

      //TODO: Change the Transaction Limitation and ordering to be taken care of in the API rather than in the front end
      const fetchTransactions = (
        customerID,
        page = 1,
        filters = '',
        transactionLimit = undefined
      ) => {
        return axios
          .get(`${BASE_URL}/customers/${customerID}/transactionsv2`, {
            headers: { Authorization: authBearer },
            params: { page: page },
          })
          .then((response) => {
            if (transactionLimit) {
              if (response?.data?.items?.length > transactionLimit) {
                response.data.items = response.data.items.splice(
                  response?.data?.items?.length - (transactionLimit + 1),
                  response?.data?.items?.length - 1
                )
              }
            }
            response.data.items = response.data.items.sort(
              (a, b) => new Date(b.datetime) - new Date(a.datetime)
            )
            return response.data
          })
          .catch((error) => {
            console.error('Fetch transactions error =>', error)
            return []
          })
      }

      const fetchTransaction = (customerID, transactionId) => {
        return axios
          .get(
            `${BASE_URL}/customers/${customerID}/transactions/${transactionId}`,
            {
              headers: { Authorization: authBearer },
            }
          )
          .then((response) => {
            return response.data
          })
          .catch((error) => {
            console.error('Fetch transaction error =>', error)
            return []
          })
      }

      const editTransaction = (transaction) => {
        let data = transaction
        return axios
          .patch(
            `${BASE_URL}/customers/${transaction.user_id}/transactions/${transaction.id}`,
            data,
            {
              headers: { Authorization: authBearer },
            }
          )
          .then((response) => {
            return response.data
          })
          .catch((error) => {
            console.error('Patch transaction error =>', error)
            return []
          })
      }

      const fetchEnterpriseTransactions = (params) => {
        return axios
          .get(`${BASE_URL}/transactions`, {
            headers: { Authorization: authBearer, Accept: 'application/pdf' },
            params: params,
          })
          .then((response) => {
            return {
              status: 'OK',
              size: response.data.size,
              data: response.data.items,
              token: response.data.continuationToken,
            }
          })
          .catch((error) => {
            let errorMessage = error.response?.data?.message
            if (errorMessage === 'Endpoint request timed out') {
              alert(
                'Endpoint request timed out. Please reduce the date range and try again.'
              )
            }
            return { status: 'FAILED', data: [], error: error }
          })
      }

      const fetchJournalEntries = (id) => {
        return axios
          .get(`${BASE_URL}/transactions/${id}`, {
            headers: { Authorization: authBearer, Accept: 'application/pdf' },
            params: { includeJELines: 'true', includeLinks: 'true' },
          })
          .then((response) => {
            return { status: 'OK', data: response.data }
          })
          .catch((error) => {
            console.log('error ====>', error)
            return { status: 'FAILED', data: [], error: error }
          })
      }

      const postJournalEntry = (body) => {
        return axios
          .post(`${BASE_URL}/journal-entries`, body, {
            headers: { Authorization: authBearer },
          })
          .then((response) => {
            return {
              status: response.data.status,
              message:
                'Successfully submitted journal entry. The journal entry will go to approval before being recorded.',
            }
          })
          .catch((error) => {
            return {
              status: 'FAILED',
              message: 'Failed to create journal entry',
              error: error,
            }
          })
      }

      const uploadTransactions = (body) => {
        return axios
          .post(`${BASE_URL}/transactions-csv`, body, {
            headers: { Authorization: authBearer },
          })
          .then((response) => {
            return {
              status: response.data.status,
              message: 'Successfully uploaded CSV.',
            }
          })
          .catch((error) => {
            return {
              status: 'FAILED',
              message: 'Failed to upload CSV',
              error: error,
            }
          })
      }

      const fetchUnreconciledSuggestions = (id) => {
        return axios
          .get(`${BASE_URL}/reconciliation/unreconciled/${id}/suggestions`, {
            headers: { Authorization: authBearer, Accept: 'application/pdf' },
          })
          .then((response) => {
            return {
              status: 'OK',
              data: response.data.unreconciledSuggestions,
            }
          })
          .catch((error) => {
            return {
              status: 'FAILED',
              message: 'Failed to fetch suggested matches to reconcile',
              error: error,
            }
          })
      }

      const reconcileTransactions = (id, transactionIds) => {
        let url = `${BASE_URL}/transactions/actions`
        return axios
          .post(
            url,
            { type: 'Link', parentId: id, childrenIds: transactionIds },
            {
              headers: { Authorization: authBearer },
            }
          )
          .then((response) => {
            return {
              status: response.data.status,
              message: 'Successfully reconciled transactions',
            }
          })
          .catch((error) => {
            return {
              status: 'failed',
              message: 'Failed to reconcile transactions',
              error: error,
            }
          })
      }

      const fetchPendingTransactions = (params) => {
        return axios
          .get(`${BASE_URL}/reconciliation/reconciled`, {
            headers: { Authorization: authBearer, Accept: 'application/pdf' },
            params: params,
          })
          .then((response) => {
            return {
              status: 'OK',
              data: response.data.reconciled,
              token: response.data.nextToken,
            }
          })
          .catch((error) => {
            let errorMessage = error.response.data.message
            if (errorMessage === 'Endpoint request timed out') {
              alert(
                'Endpoint request timed out. Please reduce the date range and try again.'
              )
            }
            return {
              status: 'FAILED',
              data: [],
              error: error,
              message: errorMessage,
            }
          })
      }

      const updateTransactionState = (data) => {
        // Action Types Disapprove | Approve | Reconcile
        let authBearer =
          TENANT_ID !== null
            ? `Bearer ${AUTH_TOKEN}:${TENANT_ID}`
            : `Bearer ${AUTH_TOKEN}`
        let url = `${BASE_URL}/transactions/actions`
        return axios.post(url, data, {
          headers: { Authorization: authBearer },
        })
      }

      const fetchNextPage = (nextURL) => {
        //TODO: Get rid of this
        return axios
          .get(`${nextURL}`, {
            headers: { Authorization: authBearer },
          })
          .then((response) => {
            return response.data
          })
      }

      const download1099B = (customerID, tries = 20) => {
        return axios
          .get(
            `${BASE_URL}/customers/${customerID}/forms/download?documentType=1099B`,
            {
              headers: { Authorization: authBearer, Accept: 'application/pdf' },
            }
          )
          .then((response) => {
            const data = response.data
            if (data.status === 'SUCCESS' && data.exists && data.url) {
              viewPdfUrl(data.url)
            } else if (
              data.status === 'SUCCESS' &&
              !data.exists &&
              tries >= 0
            ) {
              return wait(2500).then((_) =>
                download1099B(customerID, tries - 1)
              )
            }
            return data
          })
      }

      const downloadUrl = (url) => {
        const a = document.createElement('a')
        a.href = url
        a.click()
      }

      const viewPdfUrl = (url) => {
        const windowFeatures = 'menubar=no,resizable=yes,width=650,height=800'
        window.open(`${url}#toolbar=0`, 'view_pdf', windowFeatures)
      }

      const downloadCSV = (customerID) => {
        return axios
          .get(`${BASE_URL}/customers/${customerID}/transactionsv2/csv`, {
            headers: { Authorization: authBearer },
          })
          .then((response) => {
            return response.data
          })
      }

      const checkCSVForm = (customerId) => {
        //TODO: If it returns a 404 then do something...
        return axios
          .get(`${BASE_URL}/customers/${customerId}/transactionsv2/csv`, {
            headers: { Authorization: authBearer },
          })
          .then((response) => {
            return response.data
          })
      }

      const downloadCSVForm = async (params) => {
        const customerId = params.customerId
        const response = await downloadCSV(customerId)
        downloadUrl(response.url)
      }

      const fetchAuthUsers = () => {
        const errorMessage = 'Failed to fetch data for authorized users'
        return axios
          .get(`${BASE_URL}/users`, {
            headers: { Authorization: authBearer },
          })
          .then((response) => {
            return response.data.usersWithGroups
          })
          .catch((error) => {
            console.error('Error =>', error)
            console.log('Error =>', errorMessage)
            return []
          })
      }

      const fetchAuthRoles = () => {
        const errorMessage = 'Failed to fetch auth roles'
        return axios
          .get(`${BASE_URL}/users/roles`, {
            headers: { Authorization: authBearer },
          })
          .then((response) => {
            return response.data.roles
          })
          .catch((error) => {
            console.error('Error =>', error)
            console.log('Error =>', errorMessage)
            return []
          })
      }

      const editAuthUser = (props) => {
        let body = props[0]
        return axios
          .post(`${BASE_URL}/users/${body.id}`, body, {
            headers: { Authorization: authBearer },
          })
          .then((response) => {
            return response.data
          })
          .catch((error) => {
            console.error('error ===>', error)
          })
      }

      const deleteAuthUser = ({ id }) => {
        //TODO: Not tested
        return axios
          .delete(`${BASE_URL}/users/${id}`, {
            headers: { Authorization: authBearer },
          })
          .then((response) => {
            return response.data
          })
          .catch((error) => {
            console.error('Error =>', error)
          })
      }

      const fetchReports = () => {
        return axios
          .get(`${BASE_URL}/report/saved`, {
            headers: { Authorization: authBearer },
          })
          .then((response) => {
            return response.data.reports
          })
          .catch((error) => {
            console.error(error)
            return []
          })
      }

      const runSavedReport = (params) => {
        return axios
          .post(
            `${BASE_URL}/report/${params.reportId}`,
            {},
            {
              headers: { Authorization: authBearer },
            }
          )
          .then((response) => {
            return response.data
          })
          .catch((error) => {
            console.error(error)
            return []
          })
      }

      const downloadCannedReport = (params) => {
        const errorMessage = 'Failed to download saved report'
        return axios
          .post(
            `${BASE_URL}/report/${params.reportId}/download`,
            {},
            {
              headers: { Authorization: authBearer },
            }
          )
          .then((response) => {
            return response.data
          })
          .catch((error) => {
            console.error(error)
            console.log('Error =>', errorMessage)
            return []
          })
      }

      const runCustomReport = (params) => {
        return axios
          .post(
            `${BASE_URL}/report/custom`,
            {
              query: params.query,
            },
            {
              headers: { Authorization: authBearer },
            }
          )
          .then((response) => {
            return response.data
          })
          .catch((error) => {
            console.error(error)
            return []
          })
      }

      const download8949 = (params) => {
        let customerID = params.customerID
        let data = params.body
        return axios
          .post(`${BASE_URL}/customers/${customerID}/forms/8949`, data, {
            headers: { Authorization: authBearer },
          })
          .then((response) => {
            return response.data
          })
          .catch((error) => {
            console.error('Error downloading =>', error)
            return []
          })
      }

      const fetchExchangeAccounts = () => {
        let mockResponse =
          BASE_URL.includes('blockfi') || BASE_URL.includes('developers')
            ? BLOCKFI_ACCOUNTS
            : UPHOLD_ACCOUNTS
        return mockResponse
      }

      const fetchExchangeSources = () => {
        let mockResponse =
          BASE_URL.includes('blockfi') || BASE_URL.includes('developers')
            ? BLOCKFI_SOURCES
            : UPHOLD_SOURCES
        return mockResponse
      }

      const fetchEnterpriseReports = () => {
        return axios
          .get(`${BASE_URL}/reports`, {
            headers: { Authorization: authBearer },
          })
          .then((response) => {
            return response.data.reports
          })
          .catch((error) => {
            console.error(error)
            return []
          })
      }

      const postEnterpriseReport = (body) => {
        return axios
          .post(`${BASE_URL}/reports`, body, {
            headers: { Authorization: authBearer },
          })
          .then((response) => {
            return response
          })
          .catch((error) => {
            return error
          })
      }

      switch (url) {
        case 'updateCustomerMailing':
          return updateCustomerMailing(...params)
        case 'fetchCustomerAssets':
          return fetchCustomerAssets(params)
        case 'fetchTransactions':
          return fetchTransactions(...params)
        case 'fetchTransaction':
          return fetchTransaction(...params)
        case 'editTransaction':
          return editTransaction(...params)
        case 'fetchEnterpriseTransactions':
          return fetchEnterpriseTransactions(...params)
        case 'fetchJournalEntries':
          return fetchJournalEntries(...params)
        case 'postJournalEntry':
          return postJournalEntry(...params)
        case 'uploadTransactions':
          return uploadTransactions(...params)
        case 'fetchUnreconciledSuggestions':
          return fetchUnreconciledSuggestions(...params)
        case 'reconcileTransactions':
          return reconcileTransactions(...params)
        case 'fetchPendingTransactions':
          return fetchPendingTransactions(...params)
        case 'updateTransactionState':
          return updateTransactionState(...params)
        case 'fetchNextPage':
          return fetchNextPage(params)
        case 'fetchAuthUsers':
          return fetchAuthUsers(params)
        case 'editAuthUser':
          return editAuthUser(params)
        case 'deleteAuthUser':
          return deleteAuthUser(...params)
        case 'fetchAuthRoles':
          return fetchAuthRoles(params)
        case 'download1099B':
          return download1099B(params)
        case 'checkCSVForm':
          return checkCSVForm(params)
        case 'downloadCSVForm':
          return downloadCSVForm.apply(null, params)
        case 'fetchReports':
          return fetchReports(params)
        case 'runSavedReport':
          return runSavedReport(...params)
        case 'downloadCannedReport':
          return downloadCannedReport(...params)
        case 'runCustomReport':
          return runCustomReport(...params)
        case 'download8949':
          return download8949(...params)
        case 'fetchExchangeAccounts':
          return fetchExchangeAccounts(...params)
        case 'fetchExchangeSources':
          return fetchExchangeSources(...params)
        case 'fetchEnterpriseReports':
          return fetchEnterpriseReports(...params)
        case 'postEnterpriseReport':
          return postEnterpriseReport(...params)
        default:
          return console.log('no endpoint found')
      }
    }, // eslint-disable-next-line
    [appState, AUTH_TOKEN, authState, BASE_URL, TENANT_ID]
  )

  return [fetchUrl]
}

export default useFetch
